/* eslint-disable react/no-multi-comp */
import { useMemo } from 'react';

// Hooks
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils';

import PropTypes from 'prop-types';

// Lib
import { INSTAGRAM, THREADS, X } from '@/lib/socialProfile';

//Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';
import SelectWithSPIcon from '@/components/base/Select/SelectWithSPIcon';
import SelectFilter from '@/components/base/SelectFilter';
import Tooltip from '@/components/base/Tooltip';

const SpOption = ({ option, selected, active, disabled }) => {
    const { t } = useTranslation('dashboard');
    const isDisabledProfile =
        option?.social_network_type === X || option?.social_network_type === THREADS;

    const optionContent = (
        <div
            className={classNames('flex items-center', {
                'cursor-not-allowed opacity-50': disabled
            })}
            data-testid={`test-${option?.slug}`}>
            {option?.value === 'all' ? (
                <Icon
                    className={classNames('mr-px px-2 text-base', {
                        'text-icoBlue': active || selected,
                        'text-icoGray-400': disabled
                    })}
                    icon="single-profile"
                />
            ) : (
                <Avatar
                    fallback={option?.slug?.[0]}
                    size="sm"
                    profileType={option?.social_network_type}
                    url={option?.external_api_data?.avatar}
                />
            )}
            <div className="flex flex-col overflow-hidden pl-3.5 text-left">
                <span
                    className={classNames('truncate font-GSemiBold text-sm transition-all', {
                        'text-icoBlue': selected || active,
                        'text-icoBlack dark:text-white': !selected && !active && !disabled,
                        'text-icoGray-400': disabled
                    })}>
                    {option?.slug ?? option?.label}
                </span>
                {option?.value !== 'all' && (
                    <span
                        className={classNames('truncate font-GMedium text-xs transition-all', {
                            'text-icoBlue': selected || active,
                            'text-icoGray-500 dark:text-icoDarkMode-wolf':
                                !active && !selected && !disabled,
                            'text-icoGray-400': disabled
                        })}>
                        {option?.displayName ?? option?.label}
                    </span>
                )}
            </div>
        </div>
    );

    // Only wrap with Tooltip for X and Threads profiles
    if (isDisabledProfile) {
        return (
            <Tooltip
                content={t(`social_profile_disabled_tooltip.${option?.social_network_type}`)}
                placement="bottom-start"
                interactive={true}>
                <span>{optionContent}</span>
            </Tooltip>
        );
    }

    return optionContent;
};

SpOption.propTypes = {
    option: PropTypes.object,
    selected: PropTypes.bool,
    active: PropTypes.bool,
    disabled: PropTypes.bool
};

const SelectSpWidgetDashboardView = ({
    selectedSP,
    setSelectedSP,
    withAllLabel = false,
    networks = null,
    noValue = false,
    baseStyle = 'noBorder',
    avatarSize = 'sm',
    sizeStyle = 'default',
    buttonClassName = undefined
}) => {
    const { t } = useTranslation('dashboard');
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const spOptions = useMemo(() => {
        let socialProfiles = networks
            ? activatedSocialProfiles.filter((social_profile) =>
                  networks.includes(social_profile.social_network_type)
              )
            : activatedSocialProfiles;

        const options = socialProfiles.map((profile) => {
            return {
                label: profile.slug,
                displayName: profile?.external_api_data?.name ?? profile?.slug,
                value: profile.id,
                disabled:
                    profile.social_network_type === X || profile.social_network_type === THREADS,
                ...profile
            };
        });
        if (withAllLabel) {
            const allOption = {
                label: t('dashboard_select_all_social_profiles'),
                value: 'all',
                slug: t('dashboard_select_all_social_profiles')
            };
            return [allOption, ...options];
        }
        return options;
    }, [networks]);

    return (
        <SelectFilter
            selected={
                spOptions?.filter((profile) => profile.id === selectedSP?.id)[0] || selectedSP
            }
            setSelected={setSelectedSP}
            noBorder
            noValue={noValue}
            className="min-w-[3.2rem]"
            idInput="select-filter-dashboard"
            options={spOptions}
            button={
                <SelectWithSPIcon
                    sizeStyle={sizeStyle}
                    avatarSize={avatarSize}
                    baseStyle={baseStyle}
                    profileData={selectedSP}
                    noValue={noValue}
                    withDisplayName
                    socialNetworkType={selectedSP?.social_network_type ?? INSTAGRAM}
                    className={buttonClassName}
                />
            }>
            <SpOption />
        </SelectFilter>
    );
};

SelectSpWidgetDashboardView.propTypes = {
    selectedSP: PropTypes.object,
    setSelectedSP: PropTypes.func,
    withAllLabel: PropTypes.bool,
    networks: PropTypes.arrayOf(PropTypes.string),
    noValue: PropTypes.bool,
    baseStyle: PropTypes.string,
    avatarSize: PropTypes.string,
    sizeStyle: PropTypes.string
};

export default SelectSpWidgetDashboardView;
